// ProfiloSocial.tsx — le parti social del profilo: link personale (@nome), statistiche, crediti, Wall of Fame.
import { Distintivi } from "../componenti/Distintivi.tsx";
import { ScegliPaese } from "../componenti/ScegliPaese.tsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Condividi } from "../gioco/Condividi.tsx";
import { aggiornaIo, aggiornaIoLocale, type Io } from "../servizi/account.ts";
import { ErroreRete } from "../servizi/api.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { vai } from "../servizi/percorso.ts";
import {
  impostaClassifica,
  impostaNomeUtente,
  leggiStatistiche,
  linkPersonale,
  PAESI,
  type Statistiche,
} from "../servizi/social.ts";
import { BASE_API, IMP_APP, PERCORSO_CLASSIFICA, PERCORSO_NEGOZIO, PERCORSO_PRIVACY } from "../costanti.ts";
import { InterruttoriScelte } from "../componenti/InterruttoriScelte.tsx";
import { InvitaRubrica } from "../componenti/InvitaRubrica.tsx";
import { ScegliChiSfida } from "../social/ChiPuoSfidare.tsx";

const codice = (e: unknown) => (e instanceof ErroreRete ? e.codice : "generico");

export function LinkPersonale({ io }: { io: Io }) {
  const { t } = useTranslation();
  const [nome, setNome] = useState(io.nomeUtente ?? "");
  const [errore, setErrore] = useState<string | null>(null);
  const [invio, setInvio] = useState(false);
  if (!leggiImpostazione(IMP_APP.linkPersonaleAttivo, true, eBooleano)) return null;

  const salva = async () => {
    setErrore(null);
    setInvio(true);
    try {
      await impostaNomeUtente(nome);
      await aggiornaIo();
    } catch (e) {
      setErrore(codice(e));
    } finally {
      setInvio(false);
    }
  };

  return (
    <section className="scheda-account" aria-labelledby="titolo-link">
      <h2 className="scheda-titolo" id="titolo-link">
        {t("social.link_titolo")}
      </h2>
      <p className="nota">{t("social.link_testo")}</p>
      {/* v043: le spiegazioni stanno sopra l'opzione, non sotto */}
      <p className="nota">{t("social.nome_utente_regole")}</p>
      {io.nomeUtente && <p className="nota">{t("social.cambia_nome_nota")}</p>}
      <form
        className="riga-nome"
        onSubmit={(e) => {
          e.preventDefault();
          void salva();
        }}
      >
        <label className="campo-etichettato">
          <span className="etichetta">{t("social.nome_utente")}</span>
          <span className="campo-con-prefisso">
            <span aria-hidden="true">@</span>
            <input
              className="campo"
              value={nome}
              maxLength={20}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              pattern="[A-Za-z0-9_.]{3,20}"
              onChange={(e) => setNome(e.target.value.replace(/^@/, ""))}
              required
            />
          </span>
        </label>
        {/* v040: il pulsante c'è solo quando c'è qualcosa da salvare (niente pulsanti spenti) */}
        {(!io.nomeUtente || nome.trim().toLowerCase() !== io.nomeUtente) && (
          <button type="submit" className="pulsante pulsante-secondario" disabled={invio}>
            {io.nomeUtente ? t("social.cambia_nome_utente") : t("social.scegli_nome_utente")}
          </button>
        )}
      </form>
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`social.errore_${errore}`, { defaultValue: t("account.errore_generico") })}
        </p>
      )}
      {io.nomeUtente && (
        <>
          <Condividi link={linkPersonale(io.nomeUtente)} messaggio={t("social.messaggio_link")} mostraLink />
          {/* v046: il link personale anche alle persone della rubrica */}
          <InvitaRubrica link={linkPersonale(io.nomeUtente)} testo={t("social.messaggio_link")} />
          {!io.puoLink ? (
            // Sfide da sconosciuti solo dai maggiorenni: il link si vede ma non si accende.
            <p className="nota">{t("social.link_solo_maggiorenni")}</p>
          ) : (
            // v033: «Chi può sfidarti» (nessuno, chi ha il mio link, chiunque) al posto dell'interruttore
            <>
              <p className="etichetta ld-chi-sfida-titolo">{t("amici.chi_sfida")}</p>
              <ScegliChiSfida io={io} onCambiato={() => void aggiornaIo().catch(() => undefined)} />
            </>
          )}
        </>
      )}
    </section>
  );
}

export function SchedaStatistiche() {
  const { t } = useTranslation();
  const [s, setS] = useState<Statistiche | null>(null);
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    leggiStatistiche()
      .then(setS)
      .catch(() => setErrore(true));
  }, []);
  return (
    <section className="scheda-account" aria-labelledby="titolo-statistiche">
      <h2 className="scheda-titolo" id="titolo-statistiche">
        {t("social.statistiche")}
      </h2>
      <p className="nota">{t("social.stat_valide_nota")}</p>
      {errore && <p className="nota">{t("account.errore_generico")}</p>}
      {!s && !errore && <p className="nota" aria-busy="true">{t("generale.caricamento")}</p>}
      {s && (
        <dl className="statistiche">
          <div>
            <dt>{t("social.stat_giocate")}</dt>
            <dd>{s.giocate}</dd>
          </div>
          <div>
            <dt>{t("social.stat_vinte")}</dt>
            <dd>{s.vinte}</dd>
          </div>
          <div>
            <dt>{t("social.stat_perse")}</dt>
            <dd>{s.perse}</dd>
          </div>
          <div>
            <dt>{t("social.stat_valide_vinte")}</dt>
            <dd>{s.valideVinte}</dd>
          </div>
          <div>
            <dt>{t("social.stat_resistenza")}</dt>
            <dd>{t("social.secondi", { n: Math.floor(s.resistenzaMassimaMs / 1000) })}</dd>
          </div>
        </dl>
      )}
      {/* v037: fatti conoscere: quante persone hai portato e i distintivi */}
      {s && <Distintivi portati={s.portati ?? 0} />}
    </section>
  );
}

export function SchedaCrediti({ io }: { io: Io }) {
  const { t } = useTranslation();
  const negozio = leggiImpostazione(IMP_APP.negozioAttivo, true, eBooleano);
  return (
    <section className="scheda-account" aria-labelledby="titolo-crediti">
      <h2 className="scheda-titolo" id="titolo-crediti">
        {t("social.crediti")}
      </h2>
      <p className="saldo-crediti" data-crediti={io.crediti}>
        {t("social.crediti_saldo", { n: io.crediti })}
      </p>
      <p className="nota">{t("social.crediti_come")}</p>
      {negozio && (
        <button type="button" className="pulsante pulsante-primario" onClick={() => vai(PERCORSO_NEGOZIO)}>
          {t("social.negozio")}
        </button>
      )}
    </section>
  );
}

export function SchedaClassifica({ io }: { io: Io }) {
  const { t, i18n } = useTranslation();
  const [errore, setErrore] = useState<string | null>(null);
  const [avviso, setAvviso] = useState<string | null>(null);
  const [scelta, setScelta] = useState({ inClassifica: io.inClassifica, paese: io.paese });
  useEffect(() => setScelta({ inClassifica: io.inClassifica, paese: io.paese }), [io.inClassifica, io.paese]);
  const nomiPaesi = useMemo(() => {
    let dn: Intl.DisplayNames | null = null;
    try {
      dn = new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      dn = null;
    }
    return PAESI.map((c) => ({ c, n: dn?.of(c) ?? c })).sort((a, b) => a.n.localeCompare(b.n, i18n.language));
  }, [i18n.language]);
  if (!leggiImpostazione(IMP_APP.classificaAttiva, true, eBooleano)) return null;

  const salva = (inClassifica: boolean, paese: string | null) => {
    setErrore(null);
    setAvviso(null);
    const prima = scelta;
    setScelta({ inClassifica, paese });
    impostaClassifica(inClassifica, paese)
      .then((r) => {
        if (r.paeseBloccato && paese !== io.paese) {
          setAvviso(t("social.paese_bloccato"));
          // Il server ha tenuto il paese di prima: la scelta mostrata torna quella vera.
          setScelta({ inClassifica, paese: r.paese ?? null });
        }
        return aggiornaIo();
      })
      .catch((e: unknown) => {
        setScelta(prima);
        setErrore(codice(e));
      });
  };

  return (
    <section className="scheda-account" aria-labelledby="titolo-classifica">
      <h2 className="scheda-titolo" id="titolo-classifica">
        {t("social.wall_of_fame")}
      </h2>
      {!io.puoClassifica ? (
        <p className="nota">{t("social.classifica_solo_maggiorenni")}</p>
      ) : (
        <>
          {/* v040: «Compari nel Wall of Fame» è in «Le tue scelte» (è un consenso: si dà una volta e si cambia lì) */}
          <p className="nota">{scelta.inClassifica ? t("social.in_classifica_si") : t("social.in_classifica_no")}</p>
          <p className="nota">{t("social.paese_nota")}</p>
          <ScegliPaese paesi={nomiPaesi} valore={scelta.paese ?? null} onScegli={(c) => salva(scelta.inClassifica, c)} etichetta={t("social.paese")} nessuno={t("social.paese_nessuno")} />
        </>
      )}
      {avviso && <p className="nota" role="status">{avviso}</p>}
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`social.errore_${errore}`, { defaultValue: t("account.errore_generico") })}
        </p>
      )}
      <button type="button" className="pulsante pulsante-secondario" onClick={() => vai(PERCORSO_CLASSIFICA)}>
        {t("social.vedi_classifica")}
      </button>
    </section>
  );
}

/** v040: «Le tue scelte» nel profilo: foto, video delle risate, video online e Wall of Fame. */
export function SchedaScelte({ io }: { io: Io }) {
  const { t, i18n } = useTranslation();
  const [inClassifica, setInClassifica] = useState(io.inClassifica);
  const [errore, setErrore] = useState(false);
  useEffect(() => setInClassifica(io.inClassifica), [io.inClassifica]);
  const classifica = leggiImpostazione(IMP_APP.classificaAttiva, true, eBooleano) && io.puoClassifica;
  return (
    <section className="scheda-account" aria-labelledby="titolo-scelte">
      <h2 className="scheda-titolo" id="titolo-scelte">
        {t("scelte.titolo")}
      </h2>
      <p className="nota">{t("scelte.profilo_intro")}</p>
      <InterruttoriScelte />
      {classifica && (
        <label className="ld-volto-spunta ld-scelta">
          <input
            type="checkbox"
            checked={inClassifica}
            onChange={(e) => {
              const si = e.target.checked;
              setErrore(false);
              setInClassifica(si);
              aggiornaIoLocale({ inClassifica: si });
              impostaClassifica(si, io.paese)
                .then(() => aggiornaIo())
                .catch(() => {
                  aggiornaIoLocale({ inClassifica: !si });
                  setInClassifica(!si);
                  setErrore(true);
                });
            }}
          />
          <span className="ld-scelta-testo">
            <span>
              <span aria-hidden="true">🏆 </span>
              {t("social.compari_classifica")}
            </span>
            <span className="ld-scelta-nota">{t("scelte.classifica_nota")}</span>
          </span>
        </label>
      )}
      {errore && (
        <p className="admin-errore" role="alert">
          {t("account.errore_generico")}
        </p>
      )}
      <a className="ld-volto-link" href={`${BASE_API}${PERCORSO_PRIVACY}?lingua=${encodeURIComponent(i18n.language)}`} target="_blank" rel="noopener">
        {t("volto.link")}
      </a>
    </section>
  );
}
